<template>
	<div class="container pt-6 pb-10">
		<div class="flex flex-row justify-between items-center py-2 mb-4">
			<div class="flex flex-row justify-start items-center gap-8">
				<v-btn color="grey darken-1" class="uppercase" dark :to="parentListPath" exact>
					<v-icon>mdi-chevron-left</v-icon>
					Inapoi
				</v-btn>
				<h1 class="page-title-secondary">
					Contract productie nr. {{ $route.params.id }}
					<span class="px-3">{{ getFlag(contract.country) }}</span>
				</h1>
			</div>
			<div v-if="!loadingContract" class="flex flex-row justify-end items-center gap-2">
				<v-btn v-if="contractWork" :color="contractWorkStage.color" class="uppercase" dark
					:to="'/lucrari/' + contractWork.id_work">
					<v-icon left>mdi-robot-industrial</v-icon>
					Lucrare: {{ contractWorkStage.text }}
				</v-btn>
				<!-- A contract can be amended more than once: its acts, oldest first, and a new one -->
				<v-menu offset-y>
					<template v-slot:activator="{ on, attrs }">
						<v-btn color="blue white-text" class="uppercase" v-bind="attrs" v-on="on">
							<v-icon class="mr-1">mdi-file-document-plus</v-icon>
							Acte aditionale
							<span v-if="additionals.length" class="ml-1">({{ additionals.length }})</span>
							<v-icon right>mdi-menu-down</v-icon>
						</v-btn>
					</template>
					<v-list dense>
						<v-list-item v-for="act in additionals" :key="act.id_aditional" link
							:to="`/productie/contracte/single/${$route.params.id}/aditional/${act.id_aditional}`">
							<v-list-item-content>
								<v-list-item-title>Act aditional nr. {{ act.id_aditional }} / {{ formatDate(act.data) }}</v-list-item-title>
								<v-list-item-subtitle>Oferta nr. {{ act.previous_offer_id }} → nr. {{ act.new_offer_id }}</v-list-item-subtitle>
							</v-list-item-content>
						</v-list-item>
						<v-divider v-if="additionals.length"></v-divider>
						<v-list-item link @click="visibleAdditionalModal = true">
							<v-icon class="mr-1">mdi-plus</v-icon>
							Act aditional nou
						</v-list-item>
					</v-list>
				</v-menu>
				<DocPDF :contract="contract" :sending-email="sendingContractEmail" @download="downloadContractPDF"
					@sendEmail="emailContractPDF" />
				<v-btn color="teal white-text" class="uppercase" :disabled="editingMode" @click="editingMode = true">
					<v-icon>mdi-pencil</v-icon>
					Editeaza
				</v-btn>
				<!-- Only admins and superusers can delete a contract -->
				<v-menu v-if="canDeleteContract" offset-y>
					<template v-slot:activator="{ on, attrs }">
						<v-btn icon v-bind="attrs" v-on="on">
							<v-icon>mdi-dots-vertical</v-icon>
						</v-btn>
					</template>
					<v-list>
						<v-list-item link @click="visibleDeleteModal = true">
							<v-icon class="mr-1">mdi-delete-forever-outline</v-icon>
							Sterge contract
						</v-list-item>
					</v-list>
				</v-menu>
			</div>
		</div>

		<v-dialog v-model="visibleAdditionalModal" max-width="360px">
			<v-card>
				<v-form ref="additionalForm" v-model="validAdditionalForm" @submit.prevent="addNewAdditional">
					<v-card-title>
						<span class="text-h5">Genereaza act aditional</span>
					</v-card-title>
					<v-card-text>
						<p class="mb-4">Inlocuieste oferta curenta a contractului, nr. {{ contract.oferta_curenta_id || contract.oferta_id }}.</p>
						<v-text-field v-model="newOfferId" outlined dense label="Nr. oferta noua"
							:rules="[rules.required, rules.offerId]"></v-text-field>
					</v-card-text>
					<div class="flex flex-row justify-end gap-2 p-4">
						<v-btn color="blue-grey" class="uppercase white-text" large
							@click="visibleAdditionalModal = false">Anuleaza</v-btn>
						<v-btn color="teal white-text" class="uppercase" large type="submit">Salveaza</v-btn>
					</div>
				</v-form>
			</v-card>
		</v-dialog>

		<!-- MAIN SECTION -->
		<div v-if="contract.text_content" class="mb-4">
			<i class="material-icons left text-yellow-500">warning</i>
			Contract editat fata de forma initiala!
		</div>
		<div v-if="!loadingContract" class="flex justify-center relative">
			<div v-if="editingMode"
				class="absolute top-4 right-4 rounded-lg bg-white bg-opacity-50 backdrop-filter backdrop-blur-lg shadow-lg p-2 z-10 flex gap-2">
				<v-btn color="blue-grey" class="uppercase white-text" @click="editingMode = false">Anuleaza</v-btn>
				<v-btn color="teal white-text" class="uppercase white-text"
					@click="saveContractContent">Salveaza</v-btn>
			</div>
			<editor v-if="firma_montaj_contract && contract.id_contract && contract.id_contract == $route.params.id"
				v-model="document_text" :editingMode="editingMode"
				class="h-screen bg-white p-6 mb-8 overflow-y-scroll border-2 shadow-inner"
				:class="{ 'border-red-500': editingMode }" />
		</div>

		<pre v-if="$isDev" class="debug">
	<!-- <code>{{ contract }}</code> -->
	<div class="content"><code>{{ document_text }}</code></div>
	<!-- <code class="content"><code>{{ contract }}</code></code> -->
</pre>

		<!-- Modal delete -->
		<v-dialog v-model="visibleDeleteModal" max-width="400px">
			<v-card class="p-8">
				<h6 class="text-xl font-bold mb-4">Sterge definitiv contractul nr. {{ contract.id_contract }}?</h6>
				<div class="flex flex-row gap-2">
					<v-btn color="blue-grey" class="uppercase white-text" large
						@click="visibleDeleteModal = false">Anuleaza</v-btn>
					<v-btn color="red" class="uppercase white-text" large @click="deleteContract({ id: contract.id_contract, user_id: user.id_user })">
						<v-icon left dark>
							mdi-delete
						</v-icon>
						Sterge
					</v-btn>
				</div>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { DateTime } from 'luxon';
import { createContractConfig } from '@/documents/contract/index.js';
import Editor from "@/components/Editor";
import DocPDF from "./DocPDF.vue";
import { getFlag, isExportOnly } from '@/utils';
import { stageInfo } from '@/utils/works';

export default {
	name: "DetaliiContractProductie",
	components: {
		Editor,
		DocPDF
	},
	data() {
		return {
			contractConfig: null,
			visibleAdditionalModal: false,
			visibleDeleteModal: false,
			editingMode: false,
			validAdditionalForm: false,
			newOfferId: null,
			modifiedDoc: null,
			sendingContractEmail: false,
			contractWork: null,
			rules: {
				required: value => !!value || 'Câmp obligatoriu',
				cui: value => !value || /^(RO)?[0-9]{2,9}$/.test(value) || 'CUI/CIF invalid',
				regcom: value => !value || /^([J|j]|[F|f]|[C|c])[0-9]{2}\/[0-9]+\/(19|20)([0-9]{2})$/.test(value) || 'Număr înregistrare incorect',
				email: value => !value || /.+@.+\..+/.test(value) || "Adresa email invalida",
				isEmailOrPhone: () => this.isEmailOrPhone || "Email sau telefon obligatoriu",
				offerId: value => !value || /^[0-9]{1,5}$/.test(value) || "Număr ofertă incorect",
			},
		};
	},
	computed: {
		// "Inapoi" goes to the contract's list (RO or EU), not browser history
		parentListPath() {
			return this.contract.country && this.contract.country != "ro"
				? "/productie/contracte/eu"
				: "/productie/contracte/ro";
		},
		...mapState('contracts', {
			contract: 'singleContract',
			loadingContract: 'loadingSingleContract',
			additionals: 'additionalsList',
		}),
		today() {
			return this.$store.getters.today;
		},
		user() {
			return this.$store.getters.loggedUser;
		},
		canDeleteContract() {
			return ["admin", "superuser"].includes(this.user.rol);
		},
		contractWorkStage() {
			return stageInfo(this.contractWork && this.contractWork.stage);
		},
		firma_montaj_contract() {
			if (this.contract.detalii) {
				if (this.contract.detalii.firma_montaj) {
					return this.$store.getters.firma_montaj_contract(
						this.contract.detalii.firma_montaj
					);
				} else {
					return {};
				}
			}
		},
		executantDetails() {
			return this.$store.getters.executant_details;
		},
		document_text: {
			get() {
				if (this.contract.text_content) {
					return this.contract.text_content;
				} else if (this.contractConfig) {
					return this.contractConfig.buildDocumentStructure();
				}
				return null;
			},
			set(newVal) {
				this.modifiedDoc = newVal;
			},
		},
	},

	watch: {
		// export agents see only EU contracts: an RO one opened by link sends them back
		"contract.country": {
			handler(country) {
				if (country === "ro" && this.contract.id_contract == this.$route.params.id && isExportOnly(this.user)) {
					M.toast({ html: "Aveti acces doar la contractele EU", classes: "orange" });
					this.$router.replace("/productie/contracte/eu");
				}
			},
			immediate: true,
		},
		contract: {
			handler() {
				this.initializeContractConfig();
			},
			deep: true
		},

		executantDetails() {
			this.initializeContractConfig();
		},

		firma_montaj_contract() {
			this.initializeContractConfig();
		}
	},

	methods: {
		...mapActions('contracts', [
			'fetchSingleContractManufacturing',
			'updateContractContent',
			'deleteContract',
			'addAdditional',
			'fetchContractAdditionals'
		]),

		getFlag,

		formatDate(isoDate) {
			return DateTime.fromISO(String(isoDate)).toFormat("dd.MM.yyyy");
		},

		initializeContractConfig() {
			if (this.contract && this.executantDetails && this.firma_montaj_contract) {
				try {
					this.contractConfig = createContractConfig(
						this.contract.country || 'ro', // Default to Romanian if no country specified
						this.contract,
						this.executantDetails,
						this.firma_montaj_contract
					);
				} catch (error) {
					console.error('Error initializing contract config:', error);
					// Fallback to Romanian if country not supported
					this.contractConfig = createContractConfig(
						'ro',
						this.contract,
						this.executantDetails,
						this.firma_montaj_contract
					);
				}
			}
		},

		async downloadContractPDF() {
			if (this.contractConfig) {
				const doc = await this.contractConfig.generatePdf(this.document_text);
				let filename = `Contract nr. ${this.contract.id_contract} din ${this.contract.data} - ${this.contract.nume}.pdf`;
				doc.save(filename);
			}
		},

		async emailContractPDF() {
			console.log("Sending email w/ pdf...");
			// Implementation for email functionality
		},

		async addNewAdditional() {
			const valid = await this.$refs.additionalForm.validate();

			if (valid) {
				let newActAditional = {
					data: this.today,
					contract_id: this.contract.id_contract,
					new_offer_id: this.newOfferId,
					user_id: this.user.id_user,
				};
				this.addAdditional(newActAditional)
				// this.$store.dispatch("addActAditional", newActAditional);
				this.visibleAdditionalModal = false
				this.$nextTick(() => {
					this.newOfferId = null
				})
			}
		},

		saveContractContent() {
			const payload = {
				id: this.$route.params.id,
				content: this.modifiedDoc,
			};
			this.updateContractContent(payload)
			this.editingMode = !this.editingMode;
		},

		toggleEditMode() {
			this.editingMode = !this.editingMode;
		}
	},
	async created() {
		this.$store.dispatch('works/findWork', { contractId: this.$route.params.id })
			.then((work) => { this.contractWork = work; });
		this.fetchContractAdditionals(this.$route.params.id);
		await this.fetchSingleContractManufacturing(this.$route.params.id);
		this.$store.dispatch("fetchFirmeMontaj");
		this.initializeContractConfig();
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="css" scoped>
.debug {
	padding: 1rem 0 0;
}

.debug pre {
	border-radius: 5px;
	color: #333;
}

.debug code {
	display: block;
	white-space: pre-wrap;
	font-size: 0.8rem;
	padding: 0.75rem 1rem;
	background-color: #e9ecef;
	color: #495057;
}
</style>
